import classNames from 'classnames';

import { Search } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SingleValue } from 'react-select';
import debounce from 'shared/utils/debounce';
import { theme } from 'styles/theme/theme';
import UDInput from 'ud-form/components/input/component';
import UDSelect, { SelectOption } from 'ud-form/components/select/component';
import UDPerPage from 'ud-ui/components/per-page/per-page';

type Props = {
  className?: string;
  filters: any;
  years: number[];
  onFilter: (changes: any) => any;
};

const ChampionshipsFilters = (props: Props) => {
  const { filters, years, onFilter, className } = props;
  const [resultsOnPage, setResultsOnPage] = useState(filters.perPage);
  const [search, setSearch] = useState<string>(filters.search ?? '');

  // Последнее значение, которое мы сами отправили наружу, — чтобы отличить эхо собственного
  // дебаунс-запроса от внешнего сброса фильтров и не затирать то, что пользователь уже дописал.
  const lastEmittedSearchRef = useRef<string>(filters.search ?? '');

  // Внешние сбросы фильтров (кнопка «Сбросить поиск», навигация по истории) должны отражаться в поле ввода
  useEffect(() => {
    const externalSearch = filters.search ?? '';

    if (externalSearch !== lastEmittedSearchRef.current) {
      lastEmittedSearchRef.current = externalSearch;
      setSearch(externalSearch);
    }
  }, [filters.search]);

  const yearsOptions = useMemo(() => {
    const options: { label: string; value: number | null }[] = [
      {
        label: 'За все время',
        value: null,
      },
    ];

    years.forEach((year) => {
      options.push({
        label: `${year}`,
        value: year,
      });
    });

    return options;
  }, [years]);

  const selectedYear = useMemo(() => {
    return yearsOptions.find((item) => item.value === (filters.year ?? null));
  }, [filters.year, yearsOptions]);

  // Дебаунс создаётся один раз: пересоздание таймера на каждое изменение filters
  // оставляло старый таймер несброшенным и порождало серию запросов при быстром вводе.
  const onFilterRef = useRef(onFilter);
  onFilterRef.current = onFilter;

  const emitSearch = useMemo(
    () =>
      debounce((value: string) => {
        lastEmittedSearchRef.current = value;
        onFilterRef.current({ search: value || undefined, page: 1 });
      }, 500),
    [],
  );

  const handleChangeSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      emitSearch(e.target.value);
    },
    [emitSearch],
  );

  const handleChangeYear = useCallback(
    (option: SingleValue<SelectOption>) => {
      onFilter({
        year: option?.value ?? undefined,
        page: 1,
      });
    },
    [onFilter],
  );

  const emitPerPage = useMemo(
    () =>
      debounce((value: number) => {
        onFilterRef.current({ perPage: value, page: 1 });
      }, 300),
    [],
  );

  const handleChangeResultsOnPage = useCallback(
    (value: number) => {
      setResultsOnPage(value);
      emitPerPage(value);
    },
    [emitPerPage],
  );

  return (
    <div className={classNames('flex w-full gap-2 px-6 py-6', className)}>
      <div className="flex w-2/3 gap-2">
        <div className="w-1/3">
          <UDSelect options={yearsOptions} value={selectedYear} onChange={handleChangeYear} />
        </div>
        <div className="w-2/3">
          <UDInput
            value={search}
            placeholder="Поиск по турнирам"
            onChange={handleChangeSearch}
            iconProps={{
              position: 'icon-left',
              component: <Search size={16} color={theme.colors.SurfaceRomanSilver30} />,
            }}
          />
        </div>
      </div>
      <div className="w-1/3">
        <UDPerPage value={resultsOnPage} onChange={handleChangeResultsOnPage} />
      </div>
    </div>
  );
};

export default ChampionshipsFilters;
